/**
 * Decisiones de costo después de recibir una compra.
 * Los precios propuestos llegan calculados por el servidor.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaTimes, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import Button from '../common/Button';

const DECISIONES = [
  { id: 'solo_costo', label: 'Solo costo' },
  { id: 'costo_y_precios', label: 'Costo y precios' },
  { id: 'pendiente', label: 'Dejar pendiente' }
];

const REDONDEOS = [
  { id: 'sin_redondeo', label: 'Centavos' },
  { id: 'arriba', label: 'Hacia arriba' },
  { id: 'abajo', label: 'Hacia abajo' },
  { id: 'multiplo_5', label: 'Múltiplo de 5' },
  { id: 'multiplo_10', label: 'Múltiplo de 10' },
  { id: 'centena', label: 'Centena' }
];

const LISTAS = [
  { id: 'precio_venta', label: 'Precio principal' },
  { id: 'mayorista', label: 'Lista 1' },
  { id: 'interior', label: 'Lista 2' },
  { id: 'posadas', label: 'Lista 3' }
];

function vacia(cambio) {
  return {
    decision: '',
    precios: { precio_venta: false, mayorista: false, interior: false, posadas: false },
    confirmar_vigencia: false
  };
}

function dinero(n) {
  if (typeof n !== 'number') return '—';
  return `$${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function CompraCostosCambioModal({
  open,
  cambios = [],
  applying = false,
  error = '',
  tipoRedondeo = 'sin_redondeo',
  resultado = null,
  alineadas = [],
  bloqueadas = [],
  incidente = null,
  onCerrarSinCambios,
  onRecuperar,
  onClose,
  onConfirm,
  onRedondeo
}) {
  const [filas, setFilas] = useState({});
  const [masivo, setMasivo] = useState('solo_costo');
  const [confirmar, setConfirmar] = useState(false);
  const abierto = useRef(false);

  useEffect(() => {
    if (!open) {
      abierto.current = false;
      return;
    }
    if (!abierto.current) {
      abierto.current = true;
      const next = {};
      for (const cambio of cambios) next[cambio.producto_id] = vacia(cambio);
      setFilas(next);
      setConfirmar(false);
      setMasivo('solo_costo');
      return;
    }
    setFilas((prev) => {
      const next = {};
      for (const cambio of cambios) next[cambio.producto_id] = prev[cambio.producto_id] || vacia(cambio);
      return next;
    });
  }, [open, cambios]);

  const puedePrecios = (cambio) => (
    !cambio.bloqueado && LISTAS.some((lista) => cambio.propuestas && cambio.propuestas[lista.id] && cambio.propuestas[lista.id].posible)
  );

  const aplicarMasivo = () => {
    setFilas((prev) => {
      const next = { ...prev };
      for (const cambio of cambios) {
        const decision = masivo === 'costo_y_precios' && !puedePrecios(cambio) ? 'pendiente' : masivo;
        const precios = { precio_venta: false, mayorista: false, interior: false, posadas: false };
        if (decision === 'costo_y_precios') {
          for (const lista of LISTAS) {
            precios[lista.id] = !!(cambio.propuestas && cambio.propuestas[lista.id] && cambio.propuestas[lista.id].posible);
          }
        }
        next[cambio.producto_id] = {
          ...(prev[cambio.producto_id] || vacia(cambio)),
          decision,
          precios
        };
      }
      return next;
    });
    setConfirmar(false);
  };

  const patch = (id, partial) => {
    setFilas((prev) => ({ ...prev, [id]: { ...prev[id], ...partial } }));
    setConfirmar(false);
  };

  const resumen = useMemo(() => {
    const counts = { solo_costo: 0, costo_y_precios: 0, pendiente: 0, vacias: 0, incompletas: 0 };
    for (const cambio of cambios) {
      const fila = filas[cambio.producto_id];
      const decision = fila && fila.decision;
      if (!decision) {
        counts.vacias += 1;
        counts.incompletas += 1;
        continue;
      }
      counts[decision] += 1;
      const sinPrecio = decision === 'costo_y_precios' && !LISTAS.some((lista) => fila.precios[lista.id]);
      const sinVigencia = decision !== 'pendiente' && cambio.requiere_confirmacion_vigencia && !fila.confirmar_vigencia;
      if (sinPrecio || sinVigencia) counts.incompletas += 1;
    }
    return counts;
  }, [cambios, filas]);

  if (!open) return null;
  const hayContenido = cambios.length > 0 || alineadas.length > 0 || bloqueadas.length > 0 || !!incidente || !!resultado;
  if (!hayContenido) return null;
  const guardado = !!resultado;

  const enviar = () => {
    if (guardado) return;
    const decisiones = cambios.map((cambio) => {
      const fila = filas[cambio.producto_id] || vacia(cambio);
      return {
        producto_id: cambio.producto_id,
        decision: fila.decision,
        precios: fila.precios,
        confirmar_vigencia: fila.confirmar_vigencia,
        costo_visto: cambio.precio_costo_anterior
      };
    });
    onConfirm(decisiones, tipoRedondeo);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-950/70 p-2 sm:items-center sm:p-4">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-e0e3-surface text-e0e3-foreground shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-white/10 px-4 py-3">
          <div>
            <h2 className="text-lg font-semibold">{guardado ? 'Resultado guardado' : 'Costos distintos'}</h2>
            <p className="text-sm text-e0e3-muted">
              {guardado
                ? 'Estas decisiones ya se guardaron. Cerrá para salir.'
                : 'El stock ya ingresó. Elegí qué hacer con cada producto.'}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="rounded-lg p-2 text-e0e3-muted hover:bg-white/10">
            <FaTimes />
          </button>
        </div>

        {guardado ? null : (
        <div className="flex flex-wrap items-end gap-2 border-b border-white/10 px-4 py-3">
          <label className="text-xs text-e0e3-muted">
            Aplicar a todos
            <select
              aria-label="Aplicar decisión a todos"
              className="mt-1 block rounded-lg border border-white/15 bg-black/30 px-2 py-1.5 text-sm text-e0e3-foreground"
              value={masivo}
              disabled={guardado}
              onChange={(e) => setMasivo(e.target.value)}
            >
              {DECISIONES.map((op) => <option key={op.id} value={op.id}>{op.label}</option>)}
            </select>
          </label>
          <Button type="button" color="secondary" onClick={aplicarMasivo} disabled={guardado}>Aplicar decisión a todos</Button>
          <label className="text-xs text-e0e3-muted">
            Redondeo
            <select
              aria-label="Redondeo comercial"
              className="mt-1 block rounded-lg border border-white/15 bg-black/30 px-2 py-1.5 text-sm text-e0e3-foreground"
              value={tipoRedondeo}
              disabled={guardado}
              onChange={(e) => onRedondeo && onRedondeo(e.target.value)}
            >
              {REDONDEOS.map((op) => <option key={op.id} value={op.id}>{op.label}</option>)}
            </select>
          </label>
        </div>
        )}

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {incidente ? (
            <div className="rounded-lg border border-amber-300/40 p-3 text-sm" aria-label="Incidente de revisión">
              <p>{incidente.detalle || 'La revisión no quedó registrada. No se tomó una decisión comercial.'}</p>
              {onRecuperar ? (
                <Button type="button" color="secondary" className="mt-2" onClick={onRecuperar}>Recuperar revisión</Button>
              ) : null}
            </div>
          ) : null}
          {alineadas.length ? (
            <div className="rounded-lg border border-white/10 p-3 text-sm" aria-label="Revisiones sin diferencia">
              <p>El costo actual coincide con el de la compra. Cerrarlas no cambia costo, precios ni stock.</p>
              <ul className="mt-2 list-disc pl-4">
                {alineadas.map((item) => <li key={item.producto_id}>{item.nombre || item.producto_id}</li>)}
              </ul>
              {onCerrarSinCambios ? (
                <Button type="button" color="secondary" className="mt-2" disabled={guardado} onClick={() => onCerrarSinCambios(alineadas.map((item) => item.producto_id))}>
                  Resuelta sin cambios
                </Button>
              ) : null}
            </div>
          ) : null}
          {bloqueadas.length ? (
            <div className="rounded-lg border border-white/10 p-3 text-sm" aria-label="Revisiones bloqueadas">
              {bloqueadas.map((item) => (
                <p key={item.producto_id}>{item.nombre || item.producto_id}: {item.message}</p>
              ))}
            </div>
          ) : null}
          {error ? <p className="rounded-lg bg-rose-500/15 px-3 py-2 text-sm text-rose-200">{error}</p> : null}
          {cambios.map((cambio) => {
            const fila = filas[cambio.producto_id] || vacia(cambio);
            const sube = cambio.direccion === 'subio';
            return (
              <article key={cambio.producto_id} className="rounded-xl border border-white/10 bg-black/20 p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h3 className="font-medium">{cambio.nombre || 'Producto'}</h3>
                    <p className="font-mono text-xs text-e0e3-muted">{cambio.codigo}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${sube ? 'bg-rose-500/15 text-rose-200' : 'bg-emerald-500/15 text-emerald-200'}`}>
                    {sube ? <FaArrowUp /> : <FaArrowDown />}
                    {cambio.diferencia_absoluta == null ? 'Costos distintos en la compra' : `${cambio.diferencia_absoluta > 0 ? '+' : ''}${dinero(cambio.diferencia_absoluta)}`}
                    {cambio.variacion_pct != null ? ` (${cambio.variacion_pct > 0 ? '+' : ''}${cambio.variacion_pct}%)` : ''}
                  </span>
                </div>
                <dl className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
                  <div>
                    <dt className="text-xs text-e0e3-muted">Costo actual</dt>
                    <dd>{cambio.costo_invalido ? 'Inválido' : dinero(cambio.precio_costo_anterior)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-e0e3-muted">Costo de compra</dt>
                    <dd>{dinero(cambio.precio_compra_unitario)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-e0e3-muted">Diferencia</dt>
                    <dd>{cambio.variacion_pct == null ? 'Sin porcentaje' : `${cambio.variacion_pct}%`}</dd>
                  </div>
                </dl>
                {cambio.bloqueado ? (
                  <p className="mt-2 text-sm text-amber-200">Esta compra trae más de un costo para el producto. Solo puede quedar pendiente.</p>
                ) : null}
                <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label={`Decisión para ${cambio.nombre || cambio.codigo}`}>
                  {DECISIONES.map((op) => {
                    const disabled = guardado || (op.id !== 'pendiente' && !!cambio.bloqueado) || (op.id === 'costo_y_precios' && !puedePrecios(cambio));
                    const active = fila.decision === op.id;
                    return (
                      <button
                        key={op.id}
                        type="button"
                        disabled={disabled}
                        aria-pressed={active}
                        onClick={() => patch(cambio.producto_id, { decision: op.id })}
                        className={`rounded-lg border px-2 py-1 text-xs ${active ? 'border-e0e3-accent bg-e0e3-accent/20' : 'border-white/15'} disabled:opacity-40`}
                      >
                        {op.label}
                      </button>
                    );
                  })}
                </div>
                {fila.decision === 'costo_y_precios' ? (
                  <div className="mt-3 space-y-1">
                    {LISTAS.map((lista) => {
                      const propuesta = cambio.propuestas && cambio.propuestas[lista.id];
                      const posible = propuesta && propuesta.posible;
                      return (
                        <label key={lista.id} className="flex items-center justify-between gap-2 text-sm">
                          <span className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              disabled={guardado || !posible}
                              checked={!!fila.precios[lista.id]}
                              onChange={(e) => patch(cambio.producto_id, {
                                precios: { ...fila.precios, [lista.id]: e.target.checked }
                              })}
                            />
                            {lista.label}
                          </span>
                          <span className="text-e0e3-muted">
                            {posible ? `${dinero(propuesta.actual)} → ${dinero(propuesta.propuesto)}` : 'Sin margen'}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ) : null}
                {cambio.requiere_confirmacion_vigencia ? (
                  <label className="mt-2 flex items-start gap-2 text-sm text-amber-100">
                    <input
                      type="checkbox"
                      disabled={guardado}
                      checked={fila.confirmar_vigencia}
                      onChange={(e) => patch(cambio.producto_id, { confirmar_vigencia: e.target.checked })}
                    />
                    El costo vigente es posterior a esta compra. Confirmo reemplazarlo.
                  </label>
                ) : null}
              </article>
            );
          })}
        </div>

        <div className="border-t border-white/10 px-4 py-3">
          {resultado ? (
            <div className="mb-3 rounded-lg border border-white/10 p-3 text-sm" aria-label="Resultado por producto">
              <p className="font-medium">{resultado.parcial ? 'Resultado parcial' : 'Resultado final'}</p>
              <p>Actualizados: {resultado.aplicados.length ? resultado.aplicados.map((item) => item.nombre).join(', ') : 'ninguno'}</p>
              <p>Pendientes: {resultado.pendientes.length ? resultado.pendientes.map((item) => item.nombre).join(', ') : 'ninguno'}</p>
              <p>Rechazados: {resultado.rechazados.length ? resultado.rechazados.map((item) => `${item.nombre}: ${item.message || item.code}`).join(' · ') : 'ninguno'}</p>
              <p>Resueltas sin cambios: {resultado.cerrados && resultado.cerrados.length ? resultado.cerrados.map((item) => item.nombre).join(', ') : 'ninguno'}</p>
            </div>
          ) : (
            <p className="mb-2 text-sm text-e0e3-muted">
              Vista previa: {resumen.solo_costo} solo costo, {resumen.costo_y_precios} con precios, {resumen.pendiente} pendientes
              {resumen.vacias ? `, ${resumen.vacias} sin elegir` : ''}.
            </p>
          )}
          {!guardado && confirmar ? (
            <p className="mb-2 text-sm">Se van a guardar esas decisiones. Las ventas ya hechas no se modifican.</p>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            {guardado ? (
              <Button type="button" color="primary" onClick={onClose}>Cerrar</Button>
            ) : (
              <>
                <Button type="button" color="secondary" onClick={onClose}>Ahora no</Button>
                {confirmar && cambios.length ? (
                  <Button type="button" color="primary" loading={applying} disabled={resumen.incompletas > 0} onClick={enviar}>
                    Confirmar decisiones
                  </Button>
                ) : cambios.length ? (
                  <Button type="button" color="primary" disabled={resumen.incompletas > 0} onClick={() => setConfirmar(true)}>
                    Ver y confirmar
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
